/**
 * Right-click context menu for the GrapesJS layer manager.
 *
 * Usage:
 *   const tearDown = setupLayerContextMenu(editor);
 *   // on unmount:
 *   tearDown();
 *
 * Behavior:
 *   - Right-click a layer to open a context menu with Delete.
 *   - Delete removes only the right-clicked layer (and its children), without
 *     changing the current canvas selection.
 *   - Deleting a parent layer removes it and all of its children.
 */

const MENU_CLASS = 'lpc-layer-context-menu';
const MENU_ITEM_CLASS = 'lpc-layer-context-menu__item';
const LAYER_SELECTOR = '.gjs-layer';

function getDeletableComponents(editor, components) {
  const wrapper = editor.getWrapper?.();
  return components.filter((comp) => {
    if (!comp || comp === wrapper) return false;
    if (comp.get && comp.get('removable') === false) return false;
    return true;
  });
}

function getLayerComponent(layerEl, editor) {
  if (!layerEl) return null;
  if (layerEl._lpcComponent) return layerEl._lpcComponent;

  const $ = editor?.$;
  if ($) {
    const model = $(layerEl).data('model');
    if (model) {
      layerEl._lpcComponent = model;
      return model;
    }
  }

  return null;
}

function backfillLayerComponents(editor) {
  const container = editor.getContainer?.() || document.getElementById('gjs');
  const layers = container?.querySelectorAll?.(LAYER_SELECTOR) || [];
  layers.forEach((layerEl) => {
    getLayerComponent(layerEl, editor);
  });
}

export function setupLayerContextMenu(editor) {
  let panelEl = null;
  let menuEl = null;
  let pendingDelete = [];
  let dismissHandlers = null;

  const hideMenu = () => {
    if (menuEl) menuEl.style.display = 'none';
    pendingDelete = [];
    if (dismissHandlers) {
      document.removeEventListener('mousedown', dismissHandlers.onMouseDown, true);
      document.removeEventListener('contextmenu', dismissHandlers.onContextMenu, true);
      document.removeEventListener('keydown', dismissHandlers.onKeyDown, true);
      dismissHandlers = null;
    }
  };

  const ensureMenu = () => {
    if (menuEl) return menuEl;
    menuEl = document.createElement('div');
    menuEl.className = MENU_CLASS;
    menuEl.setAttribute('role', 'menu');
    menuEl.style.display = 'none';
    document.body.appendChild(menuEl);
    return menuEl;
  };

  const showMenu = (x, y, components) => {
    const deletable = getDeletableComponents(editor, components);
    if (!deletable.length) return;

    pendingDelete = deletable;
    const menu = ensureMenu();
    const label = 'Delete';

    menu.innerHTML = '';
    const item = document.createElement('button');
    item.type = 'button';
    item.className = MENU_ITEM_CLASS;
    item.setAttribute('role', 'menuitem');
    item.textContent = label;
    item.addEventListener('click', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      editor.runCommand('core:component-delete', { component: pendingDelete.slice() });
      hideMenu();
    });
    menu.appendChild(item);

    menu.style.display = 'block';
    menu.style.left = '0';
    menu.style.top = '0';

    const rect = menu.getBoundingClientRect();
    const maxX = window.innerWidth - rect.width - 8;
    const maxY = window.innerHeight - rect.height - 8;
    menu.style.left = `${Math.max(8, Math.min(x, maxX))}px`;
    menu.style.top = `${Math.max(8, Math.min(y, maxY))}px`;

    const onMouseDown = (ev) => {
      if (menu.contains(ev.target)) return;
      hideMenu();
    };
    const onContextMenu = (ev) => {
      if (menu.contains(ev.target)) return;
      hideMenu();
    };
    const onKeyDown = (ev) => {
      if (ev.key === 'Escape') hideMenu();
    };

    dismissHandlers = { onMouseDown, onContextMenu, onKeyDown };
    document.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('contextmenu', onContextMenu, true);
    document.addEventListener('keydown', onKeyDown, true);
  };

  const onLayerContextMenu = (ev) => {
    const layerEl = ev.target.closest?.(LAYER_SELECTOR);
    if (!layerEl || !panelEl?.contains(layerEl)) return;

    const component = getLayerComponent(layerEl, editor);
    if (!component) return;

    const wrapper = editor.getWrapper?.();
    if (component === wrapper) return;

    ev.preventDefault();
    ev.stopPropagation();
    hideMenu();

    showMenu(ev.clientX, ev.clientY, [component]);
  };

  const onLayerRender = ({ component, el }) => {
    if (component && el) {
      el._lpcComponent = component;
    }
  };

  const attach = () => {
    detachListeners();
    const container = editor.getContainer?.() || document.getElementById('gjs');
    panelEl =
      container?.querySelector('.gjs-pn-views-container') ||
      container?.querySelector('.gjs-layers') ||
      container;
    if (!panelEl) return;
    panelEl.addEventListener('contextmenu', onLayerContextMenu);
    backfillLayerComponents(editor);
  };

  const detachListeners = () => {
    hideMenu();
    if (panelEl) {
      panelEl.removeEventListener('contextmenu', onLayerContextMenu);
      panelEl = null;
    }
  };

  const tearDown = () => {
    editor.off('load', attach);
    editor.off('layer:root', attach);
    editor.off('layer:render', onLayerRender);
    detachListeners();
    if (menuEl) {
      menuEl.remove();
      menuEl = null;
    }
  };

  editor.on('layer:render', onLayerRender);
  editor.on('load', attach);
  editor.on('layer:root', attach);
  attach();

  return tearDown;
}
